import { type SyntheticEvent, useEffect, useState } from 'react';
import { Typography, Box, Autocomplete } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import { Selector } from 'components/Form/Selectors/Selector';
import { modalInputFieldStyle } from 'styles/global.css';
import type { Asset } from 'types/asset.types';
import type { SelectOptions } from 'types/utils.types';
import type { ICreateRulePayload } from 'types/payloads/rule.payload.types';
import { validateYupSchema } from 'utils/validators/schema.validator';
import { createRuleGeneralInfo } from 'utils/schemas/rule.schema';
import { RuleModalHeader } from './RuleModalHeader';
import {
  handleSelectAssetAll,
  handleSelectAssetSingle,
  handleSelectSingle,
  handleSelectAll,
  getInitialAssetTypes,
  getAssetMakeOptions,
  getAssetModelOptions,
} from 'views/Settings/Rules/helpers/common';
import { updateNewRule } from 'store/rule.slice';
import { ThemePalette } from 'mui.theme';
import { type SelectedRule } from 'types/notification.types';

interface Props {
  open: boolean;
  handleAbort: () => void;
  handleConfirm: () => void;
  title: string;
  assets: Asset[];
  newRule: ICreateRulePayload;
  confirmCancellation?: boolean;
  enableDarkTheme: boolean;
}

/**
 *
 * The createRuleGeneralInfo modal is a modal
 * that is used to create a rule general info.
 */
export const CreateRuleGeneralInfoModal = ({
  open,
  handleAbort,
  handleConfirm,
  title,
  assets,
  newRule,
  confirmCancellation = false,
  enableDarkTheme,
}: Props) => {
  const { typography } = useTheme();
  const dispatch = useAppDispatch();
  const { rules } = useAppSelector((state) => state.ruleReducer);
  const [assetSelectOptions, setAssetSelectOptions] = useState<SelectOptions[]>(
    []
  );

  const [assetTypeOptions] = useState<SelectOptions[]>(
    getInitialAssetTypes(assets)
  );
  const [assetMakeOptions, setAssetMakeOptions] = useState<SelectOptions[]>([]);
  const [assetModelOptions, setAssetModelOptions] = useState<SelectOptions[]>(
    []
  );

  const [currentRule, setCurrentRule] = useState<ICreateRulePayload>(newRule);
  const [isValidForm, setFormValidity] = useState(false);
  const [assetFilter, setAssetFilter] = useState<{
    assetType: string;
    assetMake: SelectOptions[];
    assetModel: SelectOptions[];
  }>({
    assetType: '',
    assetMake: [],
    assetModel: [],
  });

  const { assetType, assetMake, assetModel } = assetFilter;
  const savedRules = rules
    .filter((rule) => !rule.isDeleted) // Filter out the deleted rules
    .map((rule) => ({ ...rule }));
  const [selectedRule, setSelectedRule] = useState<SelectedRule>();

  const assetDetails = (): Asset | undefined => {
    const selectedAsset = selectedRule?.deviceIds ?? [];
    if (selectedAsset?.length > 0 || newRule.assets.length > 0) {
      return assets.find(
        (asset) =>
          asset.device.deviceId === (selectedAsset[0] || newRule.assets[0].id)
      );
    }
  };
  const selectedAssetDetailType = (value: any, param: keyof Asset) => {
    if (value) {
      return value.toString();
    } else {
      const details = assetDetails();
      if (details && param) {
        return (details[param] as any).toString();
      }
    }
  };

  /**
   * This UseEffect Updates the asset make option.
   * Determined by the assetType Selection
   */
  useEffect(() => {
    const makeOptions = getAssetMakeOptions(
      assets,
      selectedAssetDetailType(assetType, 'assetType')
    );
    const modelOptions = getAssetModelOptions(
      assets,
      selectedAssetDetailType(assetType, 'assetType'),
      makeOptions
    );
    setAssetMakeOptions(makeOptions);
    setAssetFilter((prevFilter) => ({
      ...prevFilter,
      assetMake: makeOptions,
      assetModel: modelOptions,
    }));
  }, [assetType, selectedRule]);

  /**
   * This UseEffect Updates the asset model option.
   * Determined by the assetType and assetMake Selections
   */

  useEffect(() => {
    const modelOptions = getAssetModelOptions(
      assets,
      selectedAssetDetailType(assetType, 'assetType'),
      assetMake
    );
    setAssetModelOptions(modelOptions);
    setAssetFilter((prevFilter) => ({
      ...prevFilter,
      assetModel: modelOptions,
    }));
  }, [assetMake]);

  /**
   * This UseEffect Validates the form as user fills out fields
   */
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(currentRule, createRuleGeneralInfo);

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [currentRule]);

  /**
   * This UseEffect Updates the asset selection optioins.
   * Determined by the asset Filter attributes
   */
  useEffect(() => {
    const mappedAsset: SelectOptions[] = [];

    let filteredAssets = assets.filter(
      (asset) => asset.assetType === assetType
    );

    if (assetMake.length) {
      const assetMakeList = assetMake.map((assetMake) => {
        return assetMake.id;
      });
      filteredAssets = filteredAssets.filter((asset) =>
        assetMakeList.includes(asset.make)
      );
    }

    if (assetModel.length) {
      const assetModelList = assetModel.map((assetModel) => {
        return assetModel.id;
      });
      filteredAssets = filteredAssets.filter((asset) =>
        assetModelList.includes(asset.model)
      );
    }
    /**
     * Using the asset device Id as the id here since
     * we are saving the deviceID as the device list in the DB.
     */
    filteredAssets.forEach((asset) => {
      mappedAsset.push({
        id: asset.device.deviceId,
        display: asset.bumperNumber,
      });
    });
    setAssetSelectOptions(mappedAsset);

    setCurrentRule({
      ...currentRule,
      assets: mappedAsset.length > 0 ? [...mappedAsset] : currentRule.assets,
    });
  }, [assets, assetFilter]);

  const selectedAssets =
    selectedRule?.deviceIds?.map((device) => {
      const bumperNo = assets.find((asset) => asset.device.deviceId === device);
      return { id: device, display: bumperNo?.bumperNumber ?? '' };
    }) ?? newRule.assets;

  useEffect(() => {
    setAssetSelectOptions(selectedAssets);
  }, [currentRule.assets]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCurrentRule({
      ...currentRule,
      [name]: value,
    });
  };
  const handleSelect = async (
    event: SyntheticEvent<Element, Event>,
    value: string | SelectedRule | null
  ) => {
    if (value && typeof value === 'object') {
      const selectedAssets =
        (value?.deviceIds ?? value?.devices)?.map((device) => {
          const bumperNo = assets.find(
            (asset) => asset.device.deviceId === device
          );
          return { id: device, display: bumperNo?.bumperNumber ?? '' };
        }) ?? [];
      const adaptedArray = value.distributionUsers.map((item) => ({
        id: item,
        display: item,
      }));

      setCurrentRule((prevRule) => ({
        ...prevRule,
        ruleName: `${value.ruleName} - copy`,
        assets: selectedAssets,
        conditions: value.conditions,
        alertByEmail: value.alertByEmail,
        alertByUI: value.alertByUI,
        alertByNotificationCenter: value.alertByNotificationCenter,
        distributionUsers: adaptedArray,
        notificationLevel: value.notificationLevel,
      }));

      if (value.ruleId) {
        setSelectedRule(
          savedRules.find((item) => item.ruleId === value.ruleId)
        );
      }
      setAssetSelectOptions(selectedAssets);
      dispatch(
        updateNewRule({
          ruleName: `${value.ruleName} - copy`,
          assets: selectedAssets,
          conditions: value.conditions,
          alertByEmail: value.alertByEmail,
          alertByUI: value.alertByUI,
          alertByNotificationCenter: value.alertByNotificationCenter,
          distributionUsers: adaptedArray,
          notificationLevel: value.notificationLevel,
        })
      );
    }
  };

  return (
    <InteractiveModal
      initialState={open}
      title={title}
      handleAbort={handleAbort}
      SuccessButtonProps={{
        onClick: async () => {
          dispatch(updateNewRule(currentRule));
          handleConfirm();
        },
        disabled: !isValidForm,
        label: 'Next: Conditions',
      }}
      ModalContentProps={{
        gridItemProps: {
          lg: 12,
        },
      }}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      <Typography
        key="general-info-text"
        variant="medium"
        sx={{
          fontFamily: typography.fontFamily,
        }}
      >
        <RuleModalHeader
          disabledPages={[2, 3]}
          enableDarkTheme={enableDarkTheme}
        />
      </Typography>
      {/* Rule Name TextField */}
      <Autocomplete
        freeSolo
        disablePortal
        options={savedRules}
        getOptionLabel={(option) => {
          return typeof option === 'object' ? option.ruleName : option;
        }}
        value={currentRule.ruleName}
        onChange={handleSelect}
        renderInput={(params) => (
          <SWTextField
            name="ruleName"
            label="Rule Name"
            placeholder="Enter Rule Name OR Select From Existing Rule"
            TextFieldProps={{
              ...params,
              name: 'ruleName',
              value: currentRule.ruleName,
              onChange: handleChange,
              InputProps: {
                ...params.InputProps,
                sx: {
                  width: '500px',
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.black,
                  border: 'gray 1px solid',
                },
              },
            }}
            enableDarkTheme={enableDarkTheme}
          />
        )}
      />
      <Typography
        key="small-info-text"
        variant="small"
        sx={{
          paddingTop: '0px',
          color: enableDarkTheme
            ? ThemePalette.typography.white
            : ThemePalette.typography.black,
        }}
      >
        Only underscores permitted as special characters
      </Typography>

      <Box key="assetType">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.black,
          }}
        >
          Applies to
        </Typography>

        {/* Asset Types */}
        <Selector
          inputProps={{
            sx: {
              minWidth: '355px',
            },
          }}
          fallbackValue="Select asset type"
          value={selectedAssetDetailType(assetType, 'assetType')}
          onChange={(event) => {
            const selectedAssetType = selectedAssetDetailType(
              event.target.value,
              'assetType'
            );
            setAssetFilter({
              assetType: selectedAssetType,
              assetMake: [],
              assetModel: [],
            });
            // Reset existing selected assets
            setCurrentRule({
              ...currentRule,
              assets: [],
            });
          }}
          selectorOptions={assetTypeOptions}
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <Box key="assetMake">
        {/* Asset Make List */}
        <MultiSelector
          labelText="Asset Make/OEM"
          value={assetMake}
          renderValue={(selected) => {
            return `${selected?.length || 0} OEM(s) Selected`;
          }}
          handleSelectOne={(event) => {
            // Reset existing selected assets
            setCurrentRule({
              ...currentRule,
              assets: [],
            });

            const updatedAssetFilter = {
              ...assetFilter,
              assetModel: [],
            };

            handleSelectSingle(
              event,
              updatedAssetFilter,
              setAssetFilter,
              'assetMake'
            );
          }}
          handleSelectAll={() => {
            // Reset existing selected assets
            setCurrentRule({
              ...currentRule,
              assets: [],
            });

            const updatedAssetFilter = {
              ...assetFilter,
              assetModel: [],
            };

            handleSelectAll(
              assetMakeOptions,
              updatedAssetFilter,
              setAssetFilter,
              'assetMake'
            );
          }}
          selectorOptions={assetMakeOptions}
          minWidth="400px"
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <Box key="AssetmodelList">
        {/* Asset Model List */}
        <MultiSelector
          labelText="Asset Model"
          value={assetModel}
          renderValue={(selected) => {
            return `${selected?.length || 0} Model(s) Selected`;
          }}
          handleSelectOne={(event) => {
            // Reset existing selected assets
            setCurrentRule({
              ...currentRule,
              assets: [],
            });

            handleSelectSingle(
              event,
              assetFilter,
              setAssetFilter,
              'assetModel'
            );
          }}
          handleSelectAll={() => {
            // Reset existing selected assets
            setCurrentRule({
              ...currentRule,
              assets: [],
            });

            handleSelectAll(
              assetModelOptions,
              assetFilter,
              setAssetFilter,
              'assetModel'
            );
          }}
          selectorOptions={assetModelOptions}
          minWidth="400px"
          enableDarkTheme={enableDarkTheme}
        />
      </Box>

      <Box key="assetList">
        {/* Assets List */}
        <MultiSelector
          labelText="Asset List"
          value={currentRule.assets}
          renderValue={(selected) => {
            return `${selected?.length || 0} Asset(s) Selected`;
          }}
          handleSelectOne={(event) => {
            handleSelectAssetSingle(event, currentRule, setCurrentRule);
          }}
          handleSelectAll={() => {
            handleSelectAssetAll(
              assetSelectOptions,
              currentRule,
              setCurrentRule
            );
          }}
          selectorOptions={assetSelectOptions
            .slice() // Create a shallow copy of the array to avoid mutating the original
            .sort((a, b) => a.display.localeCompare(b.display))}
          minWidth="400px"
          enableDarkTheme={enableDarkTheme}
        />
      </Box>
    </InteractiveModal>
  );
};
